import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { filter, map, Observable, Subject, takeUntil } from 'rxjs';

import {
  BatteryProperties,
  DcSensorValueProperties,
  DeviceConnectionStatus,
  DeviceConnectionStatusValues,
  DeviceFirmwareVersion,
  DeviceNameCommands,
  DeviceNameProperties,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  IsStringValueProperty,
  SignalProperties,
} from '@vi-iot/client';

import { lowBatteryThreshold, lowLqiSignalThreshold } from '@common';
import { NO_VALUE } from '@pipes';
import {
  ActiveErrorData,
  ActiveErrorsStore,
  InstallationDetailsStore,
  InstallationFeaturesStore,
} from '@store';

import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GTMService } from '@services';
import { InstallationService } from '../../services/installation.service';
import { EditDeviceNameModalComponent } from '../edit-device-name-modal/edit-device-name-modal.component';
import { HeatingCircuitHelper } from '../../installation/heating-circuit-features-panel/heating-circuit.helper';

@Component({
  selector: 'app-cs-panel',
  templateUrl: './cs-panel.component.html',
  styleUrls: ['./cs-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsPanelComponent implements OnInit, OnDestroy {
  readonly featurePropertyKey = FeaturePropertyKey;

  temperature$: Observable<Feature<DcSensorValueProperties, never>>;
  humidity$: Observable<Feature<DcSensorValueProperties, never>>;
  battery$: Observable<Feature<BatteryProperties, never>>;
  wifiLqi$: Observable<Feature<SignalProperties, never>>;
  status$: Observable<Feature<DeviceConnectionStatus, never>>;
  featuresLoading$: Observable<boolean>;
  deviceErrors$: Observable<ActiveErrorData[]>;
  deviceName$: Observable<Feature<DeviceNameProperties, DeviceNameCommands>>;
  firmware$: Observable<Feature<DeviceFirmwareVersion, never>>;
  editDeviceNameDialogRef: MatDialogRef<EditDeviceNameModalComponent>;
  activeProgram: string;

  connectionStatus = DeviceConnectionStatusValues;

  noValue = NO_VALUE;
  readonly lowLqiSignalThreshold = lowLqiSignalThreshold;
  readonly lowBatteryThreshold = lowBatteryThreshold;
  readonly primaryCircuitId = '0';
  readonly heatingOperatingProgramsSchema = HeatingCircuitHelper.HeatingOperatingProgramsSchema;

  installationTimezone$: Observable<string> = this.installationDetailsStore.installationTimezone$;

  @Input() deviceId: string;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  constructor(
    public activeErrorsStore: ActiveErrorsStore,
    public installationService: InstallationService,
    private installationDetailsStore: InstallationDetailsStore,
    private dialog: MatDialog,
    private gtmService: GTMService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.temperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceTemperatureSensor,
      this.deviceId
    );
    this.humidity$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceHumiditySensor,
      this.deviceId
    );
    this.battery$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.devicePowerBattery,
      this.deviceId
    );
    this.wifiLqi$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceZigbeeLqi,
      this.deviceId
    );
    this.firmware$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.essFirmwareBundle,
      this.deviceId
    );
    this.featuresLoading$ = this.installationFeaturesStore.featuresLoading$;
    this.status$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceConnection,
      this.deviceId
    );
    this.deviceErrors$ = this.activeErrorsStore.getActiveErrorsForSRCDevice(this.deviceId);
    this.deviceName$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      DeviceNameProperties,
      DeviceNameCommands
    >(FeatureNames.deviceName, this.deviceId);

    this.installationFeaturesStore
      .selectInstallationDeviceFeature<IsStringValueProperty, never>(
        FeatureNames.circuitActiveProgram(this.primaryCircuitId)
      )
      .pipe(
        map((feature) => feature?.properties?.value?.value),
        filter((activeProgram) => !!activeProgram)
      )
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((activeProgram: string) => {
        this.activeProgram = activeProgram;
      });
  }

  openEditDeviceNameDialog(deviceId: string): void {
    this.gtmService.track('onOpen', 'Edit Device Name modal', 'singleRoomControl');
    this.editDeviceNameDialogRef = this.dialog.open(EditDeviceNameModalComponent, {
      width: '715px',
      panelClass: 'commissioning-edit-room-dialog',
      data: {
        deviceId,
      },
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
